/**
 * @fileoverview Task Tracker state hook. Filters are synced to URL query params
 * (`group`, `project`, `students`) so links are shareable.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  fetchGroups,
  fetchProjectsByGroup,
  fetchStudentsByGroup,
  fetchTaskTrackMatrix,
} from '../service/taskTrackerService'
import { decorateMatrixForReview } from '../teacherUpload'
import { useProcessingPoll } from './useProcessingPoll'
import type {
  TaskTrackGroup,
  TaskTrackMatrix,
  TaskTrackProject,
  TaskTrackStudent,
  TaskTrackerFilters,
} from '../types'

interface UseTaskTrackerReturn {
  groups: TaskTrackGroup[]
  projects: TaskTrackProject[]
  students: TaskTrackStudent[]
  matrix: TaskTrackMatrix | null
  loading: boolean
  error: string | null
  filters: TaskTrackerFilters
  setGroupId: (id: string) => void
  setProjectId: (id: string) => void
  setStudentIds: (ids: string[]) => void
  refresh: () => void
  /** Reloads the matrix without the loading skeleton (after writes made in a drawer/modal). */
  refreshSilent: () => void
}

const PARAM_GROUP = 'group'
const PARAM_PROJECT = 'project'
const PARAM_STUDENTS = 'students'

const NATURAL_SORT_OPTIONS: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' }
const byName = <T extends { name: string }>(a: T, b: T) =>
  a.name.localeCompare(b.name, undefined, NATURAL_SORT_OPTIONS)
const byFullName = <T extends { fullName: string }>(a: T, b: T) =>
  a.fullName.localeCompare(b.fullName, undefined, NATURAL_SORT_OPTIONS)

export function useTaskTracker(): UseTaskTrackerReturn {
  const [searchParams, setSearchParams] = useSearchParams()

  const [groups, setGroups] = useState<TaskTrackGroup[]>([])
  const [projects, setProjects] = useState<TaskTrackProject[]>([])
  const [students, setStudents] = useState<TaskTrackStudent[]>([])
  const [matrix, setMatrix] = useState<TaskTrackMatrix | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMatrix, setLoadingMatrix] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mountedRef = useRef(true)
  const abortRef = useRef<AbortController | null>(null)

  const filters = useMemo<TaskTrackerFilters>(() => {
    const groupId = searchParams.get(PARAM_GROUP) ?? ''
    const projectId = searchParams.get(PARAM_PROJECT) ?? ''
    const studentsParam = searchParams.get(PARAM_STUDENTS) ?? ''
    const studentIds = studentsParam ? studentsParam.split(',').filter(Boolean) : []
    return { groupId, projectId, studentIds }
  }, [searchParams])

  const updateSearchParams = useCallback(
    (newFilters: Partial<TaskTrackerFilters>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if ('groupId' in newFilters) {
            newFilters.groupId ? next.set(PARAM_GROUP, newFilters.groupId) : next.delete(PARAM_GROUP)
          }
          if ('projectId' in newFilters) {
            newFilters.projectId
              ? next.set(PARAM_PROJECT, newFilters.projectId)
              : next.delete(PARAM_PROJECT)
          }
          if ('studentIds' in newFilters) {
            const ids = newFilters.studentIds ?? []
            ids.length > 0 ? next.set(PARAM_STUDENTS, ids.join(',')) : next.delete(PARAM_STUDENTS)
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const loadGroups = useCallback(async (): Promise<TaskTrackGroup[]> => {
    try {
      const data = await fetchGroups()
      if (!mountedRef.current) return []
      const sorted = [...data].sort(byName)
      setGroups(sorted)
      return sorted
    } catch (err) {
      if (mountedRef.current) setError(err instanceof Error ? err.message : 'Failed to load groups')
      return []
    }
  }, [])

  const loadProjects = useCallback(async (groupId: string): Promise<TaskTrackProject[]> => {
    if (!groupId) {
      setProjects([])
      return []
    }
    try {
      const data = await fetchProjectsByGroup(groupId)
      if (!mountedRef.current) return []
      const sorted = [...data].sort(byName)
      setProjects(sorted)
      return sorted
    } catch (err) {
      if (mountedRef.current) setError(err instanceof Error ? err.message : 'Failed to load projects')
      return []
    }
  }, [])

  const loadStudents = useCallback(async (groupId: string, projectId: string) => {
    if (!groupId) {
      setStudents([])
      return
    }
    try {
      const data = await fetchStudentsByGroup(groupId, projectId || undefined)
      if (!mountedRef.current) return
      setStudents([...data].sort(byFullName))
    } catch (err) {
      if (mountedRef.current) setError(err instanceof Error ? err.message : 'Failed to load students')
    }
  }, [])

  // `silent` skips the loading spinner and keeps the current matrix on error —
  // used by the processing poll so the table stays put while it re-fetches in the
  // background every few seconds.
  const loadMatrix = useCallback(
    async (projectId: string, studentIds: string[], { silent = false }: { silent?: boolean } = {}) => {
      if (!projectId) {
        setMatrix(null)
        return
      }
      abortRef.current?.abort()
      const abort = new AbortController()
      abortRef.current = abort
      if (!silent) {
        setLoadingMatrix(true)
        setError(null)
      }
      try {
        const studentId = studentIds.length === 1 ? studentIds[0] : undefined
        // AR-924 stage 1: the mock port decorates the matrix for the UI review (no-op once the HTTP adapter lands).
        const data = decorateMatrixForReview(await fetchTaskTrackMatrix(projectId, studentId))
        if (abort.signal.aborted || !mountedRef.current) return
        const filtered =
          studentIds.length > 1
            ? { ...data, students: data.students.filter((s) => studentIds.includes(s.id)) }
            : data
        setMatrix(filtered)
      } catch (err) {
        if (abort.signal.aborted || !mountedRef.current || silent) return
        setError(err instanceof Error ? err.message : 'Failed to load data')
        setMatrix(null)
      } finally {
        if (!silent && !abort.signal.aborted && mountedRef.current) setLoadingMatrix(false)
      }
    },
    [],
  )

  useEffect(() => {
    let cancelled = false
    const init = async () => {
      setLoading(true)
      const loadedGroups = await loadGroups()
      if (cancelled || !mountedRef.current) return
      if (filters.groupId && loadedGroups.some((g) => g.id === filters.groupId)) {
        const loadedProjects = await loadProjects(filters.groupId)
        await loadStudents(filters.groupId, filters.projectId)
        if (filters.projectId && loadedProjects.some((p) => p.id === filters.projectId)) {
          await loadMatrix(filters.projectId, filters.studentIds)
        }
      }
      if (!cancelled && mountedRef.current) setLoading(false)
    }
    init()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(
    () => () => {
      mountedRef.current = false
      abortRef.current?.abort()
    },
    [],
  )

  const setGroupId = useCallback(
    (id: string) => {
      updateSearchParams({ groupId: id, projectId: '', studentIds: [] })
      setMatrix(null)
      if (id) {
        loadProjects(id)
        loadStudents(id, '')
      } else {
        setProjects([])
        setStudents([])
      }
    },
    [updateSearchParams, loadProjects, loadStudents],
  )

  const setProjectId = useCallback(
    (id: string) => {
      updateSearchParams({ projectId: id, studentIds: [] })
      if (id && filters.groupId) {
        loadStudents(filters.groupId, id)
        loadMatrix(id, [])
      } else {
        setMatrix(null)
      }
    },
    [updateSearchParams, filters.groupId, loadStudents, loadMatrix],
  )

  const setStudentIds = useCallback(
    (ids: string[]) => {
      updateSearchParams({ studentIds: ids })
      if (filters.projectId) loadMatrix(filters.projectId, ids)
    },
    [updateSearchParams, filters.projectId, loadMatrix],
  )

  const refresh = useCallback(() => {
    if (filters.projectId) loadMatrix(filters.projectId, filters.studentIds)
  }, [filters.projectId, filters.studentIds, loadMatrix])

  const refreshSilent = useCallback(() => {
    if (filters.projectId) loadMatrix(filters.projectId, filters.studentIds, { silent: true })
  }, [filters.projectId, filters.studentIds, loadMatrix])

  useProcessingPoll(Boolean(matrix?.processing), refreshSilent)

  return {
    groups,
    projects,
    students,
    matrix,
    loading: loading || loadingMatrix,
    error,
    filters,
    setGroupId,
    setProjectId,
    setStudentIds,
    refresh,
    refreshSilent,
  }
}
